import { useEffect, useRef } from 'react'

import { CustomEventType } from '../types/events/CustomEventType.enum'
import { RedirectorEventDetail } from '../types/events/RedirectorEventDetail.type'

export function dispatchCustomEvent(
  eventType: CustomEventType,
  detail?: RedirectorEventDetail,
  target: EventTarget = window
) {
  const event = new CustomEvent(eventType, { detail })
  target.dispatchEvent(event)
}

/**
 * A custom React hook to add an event listener to a target element.
 *
 * Note: This hook is not intended to replace native React event handling.
 * It is designed for integrating with runtime scripts and third-party tools such as analytics systems.
 */
export function useEventListener<T extends Event = Event>(
  eventType: CustomEventType | string,
  callback: (event: T) => void,
  target: EventTarget = window
) {
  const savedCallback = useRef<(event: T) => void>()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    if (!target?.addEventListener) return

    const eventListener = (event: Event) => {
      savedCallback.current?.(event as T)
    }

    target.addEventListener(eventType, eventListener)

    return () => {
      target.removeEventListener(eventType, eventListener)
    }
  }, [eventType, target])
}
